import React from 'react'

import { getFullName } from 'src/Libs/utils/string-functions'
import PaperContainer from '../PaperContainer'
import { CircularProgress, Grid, IconButton, Stack, Tooltip } from '@mui/material'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import InfoText from '../InfoText'
import { InstallationStatementModel } from 'src/Libs/installation-statement/models/statement'
import { useFetchCompany } from 'src/Libs/service-dashboard'
import { format } from 'date-fns'
import { J2Delete, J2Editacontent } from '@e3dc-react/icons'
import { FileWithLatestStatus } from 'src/Libs/installation-statement/models'
import { Box } from '@mui/system'
import useModals from 'src/Hooks/useModals'
import { Typography } from '@e3dc-react/shell/Elements/Typography'
import { TextField } from 'src/Libs/inputs/react-hook-form'
import { useChangeWarrantyFormInitializer, useWarrantyInfo } from 'src/Libs/installation-details/hooks'
import { ChangeWarrantyForm } from 'src/Libs/installation-details/models/change-warranty.model'
import { ChangeReasonForm } from 'src/Libs/form'
import { UseQueryResult } from '@tanstack/react-query'

interface InformationProps {
  installationStatement: InstallationStatementModel | undefined
  installationDate: Date | undefined
  warrantyDate: Date | undefined
  guaranteeDate: Date | undefined
  files: FileWithLatestStatus[]
  installationId?: string
  deviceLinkId?: number
  refetchFiles: () => Promise<UseQueryResult>
  refetchInstallationStatement: () => Promise<UseQueryResult>
}

const ChangeWarrantyDialogContent: React.FC<{ isLoading: boolean; hasValidStatement: boolean }> = ({ isLoading, hasValidStatement }) => {
  const { t } = useCustomTranslation()

  return (
    <Stack gap={4}>
      <Typography>
        {hasValidStatement
          ? 'system-details.installation-statements.informations.change-warranty.description'
          : 'system-details.installation-statements.informations.change-warranty.description-invalid-statement'}
      </Typography>
      {isLoading && (
        <Box display={'flex'} justifyContent={'center'} alignItems={'center'}>
          <CircularProgress />
        </Box>
      )}
      <TextField name={'notes'} label={t('system-details.installation-statements.informations.change-warranty.note')} multiline />
    </Stack>
  )
}

/**
 * general Data about the installation statement
 */
const Information: React.FC<InformationProps> = ({
  installationStatement,
  installationDate,
  warrantyDate,
  guaranteeDate,
  files,
  installationId,
  deviceLinkId,
  refetchFiles,
  refetchInstallationStatement,
}) => {
  const { t } = useCustomTranslation()
  const [userCompany] = useFetchCompany(installationStatement?.installerCompanyId ?? undefined)
  const { openReasonDialog } = useModals()
  const { updateWarrantyNote, revokeWarranty, warrantyInfo, isLoading, refetchWarrantyInfo } = useWarrantyInfo(
    installationId,
    deviceLinkId,
    installationStatement?.id
  )
  const { initialValues, validationSchema } = useChangeWarrantyFormInitializer(warrantyInfo?.notes)
  const hasValidStatement = !!installationStatement?.installationRequirements

  const onSubmitWarrantyChange = async (values: ChangeReasonForm<ChangeWarrantyForm>): Promise<void> => {
    if (!deviceLinkId || !installationId || !installationStatement) return

    const payload = {
      notes: values.notes === '' ? null : values.notes,
      reason: values.reason,
    }

    await updateWarrantyNote({
      installationId,
      installationStatementId: installationStatement.id,
      deviceLinkId,
      data: payload,
    })
  }

  const getWarrantyValue = (): string => {
    if (warrantyDate) {
      return warrantyInfo?.isValid === false
        ? 'system-details.installation-statements.informations.warranty-revoked'
        : format(warrantyDate, t('common.format.date-fns-date'))
    }

    return '-'
  }

  const onSubmitRevokeWarranty = async (values: ChangeReasonForm): Promise<void> => {
    if (!deviceLinkId || !installationId || !installationStatement) return

    await revokeWarranty({
      installationId,
      installationStatementId: installationStatement.id,
      deviceLinkId,
      reason: values.reason,
    })
  }

  return (
    <PaperContainer title={t('system-details.installation-statements.informations.title')}>
      <Grid container padding={2}>
        <Grid item xs={12} sm={4} md={3}>
          <InfoText
            title={t('system-details.installation-statements.informations.installation-date')}
            value={installationDate ? format(installationDate, t('common.format.date-fns-date')) : '-'}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <InfoText
            title={t('system-details.installation-statements.informations.guarantee-date')}
            value={guaranteeDate ? format(guaranteeDate, t('common.format.date-fns-date')) : '-'}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3} display="flex" alignItems="center" gap={1}>
          <InfoText
            title={t('system-details.installation-statements.informations.warranty-date')}
            value={getWarrantyValue()}
            iconPosition={'right'}
            icon={
              <Tooltip title={!files.length ? t('system-details.installation-statements.informations.no-documents') : undefined}>
                <Box component={'span'}>
                  <IconButton
                    size={'small'}
                    disabled={!files.length}
                    onClick={() =>
                      openReasonDialog({
                        title: 'system-details.installation-statements.informations.change-warranty.title',
                        description: <ChangeWarrantyDialogContent isLoading={isLoading} hasValidStatement={hasValidStatement} />,
                        additionalInitialValues: initialValues,
                        additionalValidationSchema: validationSchema,
                        reasonLabel: t('system-details.installation-statements.informations.change-warranty.reason-label'),
                        onSubmit: onSubmitWarrantyChange,
                        onClose: () => {
                          Promise.all([refetchInstallationStatement(), refetchWarrantyInfo(), refetchFiles()])
                        },
                      })
                    }
                  >
                    <J2Editacontent />
                  </IconButton>
                  <IconButton
                    size={'small'}
                    disabled={!files.length}
                    onClick={() =>
                      openReasonDialog({
                        title: 'system-details.installation-statements.revoke-warranty.title',
                        description: <Typography variant="body1">{'system-details.installation-statements.revoke-warranty.description'}</Typography>,
                        onSubmit: onSubmitRevokeWarranty,
                        onClose: async () => {
                          await refetchWarrantyInfo()
                        },
                      })
                    }
                  >
                    <J2Delete />
                  </IconButton>
                </Box>
              </Tooltip>
            }
          />
        </Grid>
      </Grid>
      <Grid container padding={2}>
        <Grid item xs={12} sm={4} md={3}>
          <InfoText
            title={t('system-details.installation-statements.informations.installer-name')}
            value={getFullName(installationStatement?.installerFirstName ?? undefined, installationStatement?.installerLastName ?? undefined) || '-'}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <InfoText title={t('system-details.installation-statements.informations.installer-company')} value={userCompany?.name ?? '-'} />
        </Grid>
      </Grid>
    </PaperContainer>
  )
}

export default Information
